import flatten from 'lodash/flatten';
import { MutationType, TargetType } from '@tencent/slide-sdk-pb-model';
import defaultTableStyle from '../template/default-table-style-template.json';
import { getThemeKeyframe } from './get-theme-keyframe';
import { getMasterKeyframe, getNoteMasterKeyframe, getSlideKeyframe } from './get-page-keyframe';
import { getSectionKeyframe } from './get-section-keyframe';
import { getResourceMapKeyframe } from './get-resource-map-keyframe';
import { getDataExtensionKeyframe } from './get-data-extension-keyframe';
import type { IPresentationData } from '../data';
import type { KeyframeContext, KeyframeOptions } from './keyframe-context.interface';
import type {
    IValidMutationData,
    IValidSetNotesSizeMutationData,
    IValidSetPresentationPropertiesMutationData,
    IValidSetSlideSizeMutationData,
} from '../mutation/base';

const TENCENT_TABLE_STYLE_ID = '{58542034-FE4F-4ADA-92B8-4CA66D0F0DF3}';

export function getPresentationKeyframe(
    presentation: IPresentationData,
    options: Partial<KeyframeOptions> = {},
): IValidMutationData[] {
    const context = { presentation, options, idFactory: (id: string) => id };

    const themeKeyframes = presentation.themeList.map((theme) => getThemeKeyframe(theme));
    const masterKeyframes = presentation.masterList.map((master, index) => getMasterKeyframe(master, index, context));
    const slideKeyframes = presentation.slideList.map((slide, index) => getSlideKeyframe(slide, index, context));
    const sectionKeyframes = Object.values(presentation.sectionMap).map((section) =>
        getSectionKeyframe(section, context),
    );
    const notesMasterKeyframes = Object.values(presentation.notesMasterMap).map((notesMaster) =>
        getNoteMasterKeyframe(notesMaster, context),
    );
    const resourceMapKeyframes = getResourceMapKeyframe(presentation.resourceMap, context);

    return flatten([
        getPresentationPropsKeyframe(presentation, context),
        getDataExtensionKeyframe(TargetType.Presentation, presentation),
        flatten(resourceMapKeyframes),
        flatten(themeKeyframes),
        flatten(masterKeyframes),
        flatten(notesMasterKeyframes),
        flatten(slideKeyframes),
        flatten(sectionKeyframes),
    ]);
}

export function getPresentationPropsKeyframe(props: IPresentationData, context: KeyframeContext): IValidMutationData[] {
    void context;
    // 判断是否有默认的表格样式，没有的话需要添加
    const { tblStyle } = props.tblStyleList ?? {};
    const hasTencentTableStyle = tblStyle?.some((style) => style.styleId === TENCENT_TABLE_STYLE_ID);

    return [
        {
            type: MutationType.SetSlideSize,
            size: props.sldSz,
        } satisfies IValidSetSlideSizeMutationData,
        {
            type: MutationType.SetPresentationProperties,
            defaultTextStyle: props.defaultTextStyle,
            tableStyleList: {
                ...props.tblStyleList,
                tblStyle: hasTencentTableStyle
                    ? tblStyle
                    : (tblStyle ?? []).concat(defaultTableStyle.tblStyleList.tblStyle.slice(0, 1)),
            },
            embeddedFontList: props.embeddedFontList,
            showSpecialPlsOnTitleSld: props.showSpecialPlsOnTitleSld,
            firstSlideNum: props.firstSlideNum,
            slideGuideList: props.slideGuideList && { guide: props.slideGuideList },
            modifyVerifier: props.modifyVerifier,
        } satisfies IValidSetPresentationPropertiesMutationData,
        {
            type: MutationType.SetNotesSize,
            size: props.notesSz,
        } satisfies IValidSetNotesSizeMutationData,
    ];
}
